import { ReactRenderer } from '@tiptap/react'
import Suggestion from '@tiptap/suggestion'

import type { MentionDto } from '@/entities/mentions'
import { updateTiptapSuggestionsPosition } from '@/entities/tiptap-suggestions/lib/update-tiptap-suggestions-position'

import type {
  MentionSuggestionsCommandReturn,
  MentionSuggestionsHandle,
} from './mentions.config'
import { mentionsExtensionConfig } from './mentions.config'
import { MentionSuggestionsList } from './MentionsSuggestionsList'

import type { Editor } from '@tiptap/react'

export function createMentionsSuggestion(options: {
  name: string
  editor: Editor
}) {
  return Suggestion<MentionDto, MentionSuggestionsCommandReturn>({
    pluginKey: mentionsExtensionConfig.pluginKey,
    editor: options.editor,
    char: mentionsExtensionConfig.character,
    allowSpaces: false,
    startOfLine: false,

    render() {
      let component:
        | ReactRenderer<
            MentionSuggestionsHandle,
            React.ComponentProps<typeof MentionSuggestionsList>
          >
        | undefined

      return {
        onStart(props) {
          if (!props.editor.isFocused) return

          component = new ReactRenderer(MentionSuggestionsList, {
            props: {
              ...props,
              onSelect: (mention: MentionDto) => {
                props.command({ mention })
                component?.destroy()
              },
            },
            editor: props.editor,
          })

          if (component.element instanceof HTMLElement) {
            component.element.style.position = 'absolute'

            // Append to body to allow positioning outside the editor boundaries
            document.body.appendChild(component.element)

            updateTiptapSuggestionsPosition(props.editor, component.element)
          }
        },
        onUpdate(props) {
          component?.updateProps(props)

          if (component?.element instanceof HTMLElement) {
            updateTiptapSuggestionsPosition(props.editor, component.element)
          }
        },
        onKeyDown(props) {
          if (props.event.key === 'Escape') {
            component?.destroy()
            return true
          }

          return component?.ref?.onKeyDown(props) ?? false
        },
        onExit() {
          component?.destroy()
        },
      }
    },

    command({ editor, range, props }) {
      const { mention } = props

      editor
        .chain()
        .focus()
        .insertContentAt(range, [
          {
            type: options.name,
            attrs: {
              mentionId: mention.id,
              fallbackName: mention.name,
            },
          },
        ])
        .run()
    },
  })
}
